import { TypeBlogArticle, TypeBlogCategory, TypePage } from '@platform/contentful';
import { BlogArticleSummary } from '../util/types';

/* Utils in this file are used to filter down the whole data set of Contentful entries into just
 * Blog Articles, article categories, and the "summaries" representing the subset of data needed on the
 * /blog/category/ pages. */

const articleToSummary = (
  article: TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>,
): BlogArticleSummary => ({
  metadata: article.metadata,
  sys: article.sys,
  fields: {
    title: article.fields.title,
    slug: article.fields.slug,
    metaDescription: article.fields.metaDescription,
    categories: article.fields.categories,
    author: article.fields.author,
    bannerImage: article.fields.bannerImage,
    publishDateOverride: article.fields.publishDateOverride,
  },
});

export const getBlogArticles = (
  contentfulEntries: (
    | TypePage<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>
    | TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>
  )[],
) => {
  return (
    contentfulEntries.filter(
      (item) => item.sys.contentType.sys.id === 'blogArticle',
    ) as TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>[]
  ).sort((a, b) => {
    const aPublishedAt = new Date(a.fields.publishDateOverride ?? a.sys.createdAt);
    const bPublishedAt = new Date(b.fields.publishDateOverride ?? b.sys.createdAt);
    return aPublishedAt < bPublishedAt ? 1 : -1;
  });
};

// Collect all the category names acreoss all blog articles
// TODO: It'd be more efficient to query for the categories themselves when getting data from Contentful
export const getBlogCategories = (
  blogArticles: TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>[],
) => {
  const categories: Record<string, TypeBlogCategory<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>> = {};
  blogArticles.forEach((article) => {
    article.fields.categories.map((c) => {
      if (!c) {
        return;
      }

      categories[c.fields.slug] = c;
    });
  });
  return Object.values(categories).sort((a, b) =>
    a.fields.name.toLowerCase() > b.fields.name.toLowerCase() ? 1 : -1,
  );
};

export const getArticleSummaries = (
  blogArticles: TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>[],
) => {
  return blogArticles.map(articleToSummary);
};

export const getMostRecentSummaries = (summaries: BlogArticleSummary[]) => summaries.slice(0, 30);

export const getAllSummariesForCategory = (summaries: BlogArticleSummary[], category: string) =>
  // Include all articles of the cateogry is 'all'
  summaries.filter((article) =>
    article.fields.categories.some((c) => category === 'all' || c?.fields.slug === category),
  );

export const getBlogUrl = (slug: string) => {
  if (import.meta.env.VITE_ENVIRONMENT === 'production') {
    return `https://blog.movingworlds.org/${slug}`;
  } else {
    return `${import.meta.env['VITE_STATIC_FILES_URL']}/blog/${slug}`;
  }
};
